import ContactEmailAddressSelector from '@app/components/ContactEmailAddressSelector'
import ContactProfileImage from '@app/components/ContactProfileImage'
import { IShareContact, ShareContact } from '@app/models/ShareContact'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { List } from 'immutable'
import * as React from 'react'
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverBody,
  PopoverHeader,
} from 'reactstrap'

interface IShareModalProps {
  defaultContacts?: List<IShareContact>
  isLoading?: boolean
  isOpen: boolean
  isSaving: boolean
  onCancel: () => void
  onLoadDefaultContacts?: () => void
  onShare: (
    contacts: List<IShareContact>,
    comment: string,
    subject: string,
  ) => void
  showSubject?: boolean
  subject?: string
  title: string
}

interface IShareModalState {
  comment: string
  contacts: List<IShareContact>
  showPopover: any
  subject: string
}

export default class ShareModal extends React.PureComponent<
  IShareModalProps,
  IShareModalState
> {
  constructor(props: IShareModalProps) {
    super(props)

    this.state = {
      comment: '',
      contacts:
        (this.props.defaultContacts as List<ShareContact>) ||
        List<ShareContact>(),
      showPopover: {},
      subject: '',
    }

    bindFunctions(this)
  }

  public componentDidUpdate(prevProps: IShareModalProps) {
    if (
      this.props.defaultContacts !== undefined &&
      prevProps.defaultContacts !== this.props.defaultContacts &&
      this.props.defaultContacts.size > 0
    ) {
      const newContacts = this.state.contacts.withMutations((contacts: any) => {
        this.props.defaultContacts!.toArray().forEach((defaultContact) => {
          if (
            contacts.findIndex(
              (c: any) => c !== undefined && c.id === defaultContact.id,
            ) === -1
          ) {
            contacts.push(defaultContact as ShareContact)
          }
        })
      })
      this.setState({ contacts: newContacts })
    }
  }

  public render() {
    return (
      <Modal
        isOpen={this.props.isOpen}
        centered
        toggle={this._onCancel}
        onEnter={this._onLoadModal}
        size='lg'
      >
        <ModalHeader toggle={this._onCancel}>{this.props.title}</ModalHeader>
        <ModalBody style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          <div className='mb-2'>
            <ContactEmailAddressSelector
              disabled={this.props.isLoading || this.props.isSaving}
              onSelected={this._onAddContact}
              placeholder='Enter a group name, contact name or email address...'
              required={this.state.contacts.size === 0}
            />
          </div>
          <div className='ml-2'>
            {this.state.contacts.size > 0 && (
              <div className='flex-grow-1'>
                {this.state.contacts
                  .toArray()
                  .map((contact: IShareContact, contactIndex: number) => {
                    if (contact.type === 'Contact') {
                      return this._renderContact(contact, contactIndex)
                    } else {
                      return this._renderGroup(contact, contactIndex)
                    }
                  })}
              </div>
            )}
          </div>
          {this.props.showSubject === true && (
            <div className='mb-2'>
              <Label>Subject</Label>
              <Input
                onChange={this._onChangeSubject}
                placeholder='Add a custom subject line...'
                value={this.state.subject || ''}
              />
            </div>
          )}
          <div>
            <Label>Message</Label>
            <Input
              onChange={this._onChangeComment}
              placeholder='Include a personal message...'
              type='textarea'
              value={this.state.comment}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' outline onClick={this._onCancel}>
            <FontAwesomeIcon icon={['fal', 'times']} fixedWidth /> Cancel
          </Button>
          <Button
            color='primary'
            disabled={this.state.contacts.size === 0 || this.props.isSaving}
            onClick={this._onShare}
          >
            <FontAwesomeIcon icon={['fal', 'share-alt']} fixedWidth /> Share
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private _renderContact(contact: IShareContact, index: number) {
    return (
      <div key={contact.id} className='d-flex align-content-center py-2'>
        <div className='mr-2'>
          <ContactProfileImage contact={contact.id} size={35} />
        </div>
        <div className='flex-grow-1 mr-2'>
          <div>{contact.name}</div>
          <div className='small text-muted'>
            {contact.addresses.first().address}
          </div>
        </div>
        <div>
          <Button
            color='link'
            className='text-danger'
            onClick={() => this._onDeleteContact(index)}
          >
            <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
          </Button>
        </div>
      </div>
    )
  }

  private _renderGroup(group: IShareContact, index: number) {
    return (
      <div key={group.id} className='d-flex align-content-center py-2'>
        <div className='mr-2'>
          <FontAwesomeIcon
            icon={['fas', 'users']}
            fixedWidth
            style={{ fontSize: '1.6rem' }}
          />
        </div>
        <div className='flex-grow-1 mr-2'>
          <div>
            {group.name}{' '}
            <a
              className='text-primary'
              onClick={(e) => {
                e.stopPropagation()
                this._togglePopover(group.id)
              }}
              id={`group-${group.id}`}
            >{`(${group.addresses.size} contacts)`}</a>
          </div>
        </div>
        <div>
          <Button
            color='link'
            className='text-danger'
            onClick={() => this._onDeleteContact(index)}
          >
            <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
          </Button>
        </div>
        <Popover
          isOpen={this.state.showPopover[group.id]}
          placement='right'
          target={`group-${group.id}`}
          toggle={() => this._togglePopover(group.id)}
        >
          <PopoverHeader>Contacts in Group</PopoverHeader>
          <PopoverBody style={{ overflowY: 'auto', maxHeight: '300px' }}>
            {group.addresses.toArray().map((email) => (
              <div key={email.contactId} className='d-flex flex-column mt-2'>
                <div className='text-muted'>{`${email.displayName}`}</div>
                <div style={{ fontSize: '0.775rem' }}>
                  <a href={`mailto: ${email.address}`}>
                    {email.address ? email.address : 'No email'}
                  </a>
                </div>
              </div>
            ))}
          </PopoverBody>
        </Popover>
      </div>
    )
  }

  private _onAddContact(newContact: IShareContact) {
    const existing = this.state.contacts.findIndex((c: any) => {
      return (
        c !== undefined &&
        c.addresses.find((item: any) => {
          return newContact.type === 'Contact'
            ? item.address === newContact.addresses.first().address
            : item.displayName === newContact.name
        }) &&
        c.id === newContact.id
      )
    })

    if (
      existing === -1 &&
      newContact.addresses &&
      newContact.addresses.size > 0
    ) {
      this.setState({ contacts: this.state.contacts.push(newContact) })
    }
  }

  private _onCancel() {
    this.props.onCancel()
  }

  private _onChangeComment(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ comment: event.target.value })
  }

  private _onChangeSubject(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ subject: event.target.value })
  }

  private _onDeleteContact(index: number) {
    if (this.state.contacts.has(index)) {
      this.setState({ contacts: this.state.contacts.delete(index) })
    }
  }

  private _onLoadModal() {
    if (this.props.onLoadDefaultContacts) {
      this.props.onLoadDefaultContacts()
    }
  }

  private _onShare() {
    this.props.onShare(
      this.state.contacts,
      this.state.comment,
      this.state.subject,
    )
  }

  private _togglePopover(id: string) {
    this.setState({ showPopover: { [id]: !this.state.showPopover[id] } })
  }
}
